(() => {
  const STORAGE_KEY = 'qbs-wishlist';
  const BUTTON_SELECTOR = '[data-qbs-wishlist]';
  const COUNT_SELECTOR = '[data-qbs-wishlist-count]';
  const GRID_SELECTOR = '[data-qbs-wishlist-grid]';
  const EMPTY_SELECTOR = '[data-qbs-wishlist-empty]';
  const HEADER_SELECTOR = '[data-qbs-wishlist-header]';
  const SLIDER_SELECTOR = '[data-qbs-wishlist-slider]';

  const toId = (value) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const dedupeIds = (ids) => {
    const unique = [];
    const set = new Set();

    ids.forEach((id) => {
      if (!id || set.has(id)) return;
      set.add(id);
      unique.push(id);
    });

    return unique;
  };

  const parseLegacyState = (parsed) => {
    const ids = Array.isArray(parsed?.ids) ? parsed.ids.map(toId).filter(Boolean) : [];
    const handles = parsed?.handles && typeof parsed.handles === 'object' ? parsed.handles : {};

    return {
      ids: dedupeIds(ids),
      handles: handles,
    };
  };

  const readState = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { ids: [], handles: {} };

      const parsed = JSON.parse(raw);

      if (Array.isArray(parsed)) {
        return {
          ids: dedupeIds(parsed.map(toId).filter(Boolean)),
          handles: {},
        };
      }

      if (parsed && Array.isArray(parsed.items)) {
        const ids = [];
        const handles = {};

        parsed.items.forEach((item) => {
          const id = toId(item?.id);
          if (!id) return;
          ids.push(id);
          if (item?.handle) {
            handles[String(id)] = String(item.handle);
          }
        });

        return { ids: dedupeIds(ids), handles };
      }

      return parseLegacyState(parsed);
    } catch (_) {
      return { ids: [], handles: {} };
    }
  };

  const writeState = (state) => {
    const ids = dedupeIds((state?.ids || []).map(toId).filter(Boolean));
    const handles = {};

    ids.forEach((id) => {
      const key = String(id);
      const handle = state?.handles?.[key];
      if (typeof handle === 'string' && handle.trim() !== '') {
        handles[key] = handle.trim();
      }
    });

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ids,
        handles,
      })
    );

    return { ids, handles };
  };

  const getWishlistState = () => readState();

  const getWishlistIds = () => getWishlistState().ids;

  const getWishlistCount = () => getWishlistIds().length;

  const isInWishlist = (productId) => getWishlistIds().includes(toId(productId));

  const dispatchWishlistUpdate = () => {
    const ids = getWishlistIds();
    document.dispatchEvent(
      new CustomEvent('wishlist:update', {
        bubbles: true,
        detail: {
          count: ids.length,
          ids: ids,
        },
      })
    );
  };

  const applyButtonState = (button, isActive) => {
    button.classList.toggle('_icon-favorite-fill', isActive);
    button.classList.toggle('_icon-favorite', !isActive);
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  };

  const updateWishlistCountUi = () => {
    const count = getWishlistCount();
    const counters = document.querySelectorAll(COUNT_SELECTOR);

    counters.forEach((counter) => {
      counter.textContent = count > 99 ? '99+' : String(count);
      counter.hidden = count === 0;
    });
  };

  const syncWishlistButtons = () => {
    const ids = new Set(getWishlistIds());
    const buttons = document.querySelectorAll(BUTTON_SELECTOR);

    buttons.forEach((button) => {
      const productId = toId(button.dataset.productId);
      if (!productId) return;
      applyButtonState(button, ids.has(productId));
    });
  };

  const hydrateHandlesFromDom = () => {
    const state = getWishlistState();
    let changed = false;

    document.querySelectorAll(BUTTON_SELECTOR).forEach((button) => {
      const productId = toId(button.dataset.productId);
      const productHandle = (button.dataset.productHandle || '').trim();
      if (!productId || !productHandle) return;
      if (!state.ids.includes(productId)) return;

      const key = String(productId);
      if (state.handles[key] !== productHandle) {
        state.handles[key] = productHandle;
        changed = true;
      }
    });

    if (changed) {
      writeState(state);
    }
  };

  const toggleWishlist = (productId, productHandle = '') => {
    const id = toId(productId);
    if (!id) return false;

    const handle = String(productHandle || '').trim();
    const state = getWishlistState();
    const index = state.ids.indexOf(id);
    let isActive = false;

    if (index >= 0) {
      state.ids.splice(index, 1);
      delete state.handles[String(id)];
      isActive = false;
    } else {
      state.ids.push(id);
      if (handle) {
        state.handles[String(id)] = handle;
      }
      isActive = true;
    }

    writeState(state);
    return isActive;
  };

  const rootPath = () => {
    const rawRoot = window.Shopify?.routes?.root || '/';
    if (!rawRoot.endsWith('/')) return `${rawRoot}/`;
    return rawRoot;
  };

  const formatPrice = (cents) => {
    const value = Number(cents || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'USD';

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const getProductMinMaxPrice = (product) => {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    if (!variants.length) return { min: 0, max: 0 };

    const prices = variants
      .map((variant) => Number(variant?.price || 0))
      .filter((price) => Number.isFinite(price));

    if (!prices.length) return { min: 0, max: 0 };

    return {
      min: Math.min(...prices),
      max: Math.max(...prices),
    };
  };

  const renderWishlistCard = (product) => {
    const productId = toId(product?.id);
    const productHandle = String(product?.handle || '');
    const productUrl = String(product?.url || '#');
    const productTitle = escapeHtml(product?.title || 'Product');
    const productCategory = escapeHtml(product?.type || product?.vendor || 'Product');
    const imageUrl = product?.featured_image ? escapeHtml(product.featured_image) : '';
    const imageAlt = escapeHtml(product?.title || 'Product');
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const hasSingleVariant = variants.length === 1;
    const variantId = hasSingleVariant ? toId(variants[0]?.id) : null;
    const cartAddUrl = `${rootPath()}cart/add.js`;
    const { min, max } = getProductMinMaxPrice(product);
    const priceHtml = min !== max ? `${formatPrice(min)} - ${formatPrice(max)}` : formatPrice(min);

    if (!productId || !productHandle) return '';

    return `
      <article class="slider-products__slide item-product swiper-slide">
        <div class="item-product__top">
          <a href="${escapeHtml(productUrl)}" class="item-product__image">
            ${
              imageUrl
                ? `<img class="ibg" src="${imageUrl}" alt="${imageAlt}" loading="lazy" width="900" height="900">`
                : ''
            }
            <div class="preloader"></div>
          </a>
          <button
            type="button"
            class="item-product__asset-button asset-button asset-button_md-small _icon-favorite-fill"
            aria-label="Add to wishlist"
            aria-pressed="true"
            data-qbs-wishlist
            data-product-id="${productId}"
            data-product-handle="${escapeHtml(productHandle)}"
          ></button>
          <button
            type="button"
            class="item-product__asset-button asset-button asset-button_md-small _icon-plus"
            aria-label="Quick add"
            data-qbs-quick-add
            data-product-url="${escapeHtml(productUrl)}"
            ${variantId ? `data-variant-id="${variantId}" data-cart-add-url="${escapeHtml(cartAddUrl)}"` : ''}
          ></button>
        </div>
        <div class="item-product__content">
          <a href="${escapeHtml(productUrl)}" class="item-product__category">${productCategory}</a>
          <div class="item-product__body">
            <a href="${escapeHtml(productUrl)}" class="item-product__title"><h3>${productTitle}</h3></a>
            <div class="item-product__price price">${priceHtml}</div>
          </div>
        </div>
      </article>
    `;
  };

  const fetchProductByHandle = async (handle) => {
    const response = await fetch(`${rootPath()}products/${encodeURIComponent(handle)}.js`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) return null;
    return response.json();
  };

  const renderWishlistPage = async () => {
    const grid = document.querySelector(GRID_SELECTOR);
    if (!grid) return;

    const emptyState = document.querySelector(EMPTY_SELECTOR);
    const header = document.querySelector(HEADER_SELECTOR);
    const slider = document.querySelector(SLIDER_SELECTOR);
    const state = getWishlistState();
    const handles = state.ids
      .map((id) => state.handles[String(id)])
      .filter((handle) => typeof handle === 'string' && handle.trim() !== '');

    if (!handles.length) {
      grid.innerHTML = '';
      if (emptyState) emptyState.hidden = false;
      if (header) header.hidden = true;
      if (slider) slider.hidden = true;
      return;
    }

    const products = await Promise.all(handles.map((handle) => fetchProductByHandle(handle)));
    const cardsHtml = products
      .filter(Boolean)
      .map((product) => renderWishlistCard(product))
      .filter(Boolean)
      .join('');

    grid.innerHTML = cardsHtml;

    if (emptyState) {
      emptyState.hidden = cardsHtml !== '';
    }
    if (header) {
      header.hidden = cardsHtml === '';
    }
    if (slider) {
      slider.hidden = cardsHtml === '';
    }

    if (slider?.swiper) {
      slider.swiper.update();
      slider.swiper.slideTo(0, 0);
    }

    syncWishlistButtons();
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest(BUTTON_SELECTOR);
    if (!(button instanceof HTMLElement)) return;

    const productId = toId(button.dataset.productId);
    const productHandle = button.dataset.productHandle || '';
    if (!productId) return;

    event.preventDefault();
    toggleWishlist(productId, productHandle);
    syncWishlistButtons();
    updateWishlistCountUi();
    dispatchWishlistUpdate();
  });

  document.addEventListener('wishlist:update', () => {
    const hasWishlistGrid = document.querySelector(GRID_SELECTOR);
    if (hasWishlistGrid) {
      renderWishlistPage();
    }
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    syncWishlistButtons();
    updateWishlistCountUi();
    renderWishlistPage();
  });

  window.QbsWishlist = {
    STORAGE_KEY,
    getWishlistIds,
    toggleWishlist,
    isInWishlist,
  };

  hydrateHandlesFromDom();
  syncWishlistButtons();
  updateWishlistCountUi();
  renderWishlistPage();
})();
